import React, { useState, useEffect, useCallback, useContext } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  CardHeader, 
  IconButton, 
  Button, 
  CircularProgress, 
  Avatar,
  Paper,
  TextField,
  Modal,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import { Add, Edit, Delete, Person } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { UserAddress, TrustedContact } from '../types';
import { getAddresses, createAddress, updateAddress, deleteAddress } from '../utils/api';
import { AuthContext } from '../context/AuthContext';
import AddressForm from '../components/AddressForm';
import toast from 'react-hot-toast';
import { api } from '../utils/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: '90%', sm: 500 },
  maxWidth: 500,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  borderRadius: 2,
  maxHeight: '90vh',
  overflow: 'auto',
};

const ProfilePage: React.FC = () => {
  const auth = useContext(AuthContext);
  const user = auth?.user;
  const updateUser = auth?.updateUser;
  const [addresses, setAddresses] = useState<UserAddress[]>([]);
  const [editingAddress, setEditingAddress] = useState<UserAddress | undefined>(undefined);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [addressToDelete, setAddressToDelete] = useState<number | null>(null);
  const [isEditingPaymentType, setIsEditingPaymentType] = useState(false);
  const [paymentType, setPaymentType] = useState<string>(user?.payment_type || '');
  
  // Trusted Contacts state
  const [trustedContacts, setTrustedContacts] = useState<TrustedContact[]>([]);
  const [editingContact, setEditingContact] = useState<TrustedContact | undefined>(undefined);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState<number | null>(null);
  const [isContactDeleteModalOpen, setIsContactDeleteModalOpen] = useState(false);
  const [contactFormData, setContactFormData] = useState({
    name: '',
    title: '',
    email: '',
    phone_number: '',
  });
  const [invoiceType, setInvoiceType] = useState<string>(user?.default_invoice_type || 'one-time');
  const [isEditingInvoiceType, setIsEditingInvoiceType] = useState(false);
  const [sendEmailToTrustedContact, setSendEmailToTrustedContact] = useState<boolean>(user?.send_email_to_trusted_contact || false);
  const [isEditingSendEmailSetting, setIsEditingSendEmailSetting] = useState(false);

  useEffect(() => {
    if (user?.payment_type) {
      setPaymentType(user.payment_type);
    }
  }, [user?.payment_type]);

  useEffect(() => {
    if (user?.default_invoice_type) {
      setInvoiceType(user.default_invoice_type);
    }
    if (user?.send_email_to_trusted_contact !== undefined) {
      setSendEmailToTrustedContact(user.send_email_to_trusted_contact);
    }
  }, [user?.default_invoice_type, user?.send_email_to_trusted_contact]);

  const fetchAddresses = useCallback(async () => {
    if (user) {
      try {
        setLoading(true);
        const response = await api.get('/addresses');
        setAddresses(response.data);
      } catch (error) {
        toast.error('Failed to fetch addresses.');
        console.error(error);
      } finally {
        setLoading(false);
      }
    }
  }, [user]);

  const fetchTrustedContacts = useCallback(async () => {
    if (user) {
      try {
        const response = await api.get('/trusted-contacts');
        setTrustedContacts(response.data);
      } catch (error) {
        toast.error('Failed to fetch customer contacts.');
        console.error(error);
      }
    }
  }, [user]);

  useEffect(() => {
    fetchAddresses();
    fetchTrustedContacts();
  }, [fetchAddresses, fetchTrustedContacts]);

  const handleFormSubmit = async (addressData: Omit<UserAddress, 'id' | 'user_id'>) => {
    try {
      if (editingAddress) {
        await toast.promise(api.put(`/addresses/${editingAddress.id}`, addressData), {
          loading: 'Updating address...',
          success: 'Address updated successfully!',
          error: 'Failed to update address.'
        });
      } else {
        await toast.promise(api.post('/addresses', addressData), {
          loading: 'Creating address...',
          success: 'Address created successfully!',
          error: 'Failed to create address.'
        });
      }
      fetchAddresses();
      setIsModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleEdit = (address: UserAddress) => {
    setEditingAddress(address);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingAddress(undefined);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        await api.delete(`/addresses/${id}`);
        toast.success('Address deleted!');
        fetchAddresses();
      } catch (err) {
        console.error(err);
      }
    }
  };

  const confirmDelete = async () => {
    if (addressToDelete !== null) {
      await handleDelete(addressToDelete);
      setAddressToDelete(null);
    }
    setIsConfirmModalOpen(false);
  };

  // Trusted Contact handlers
  const handleContactEdit = (contact: TrustedContact) => {
    setEditingContact(contact);
    setContactFormData({
      name: contact.name,
      title: contact.title || '',
      email: contact.email,
      phone_number: contact.phone_number || '',
    });
    setIsContactModalOpen(true);
  };

  const handleContactAddNew = () => {
    setEditingContact(undefined);
    setContactFormData({
      name: '',
      title: '',
      email: '',
      phone_number: '',
    });
    setIsContactModalOpen(true);
  };

  const handleContactFormSubmit = async () => {
    try {
      if (editingContact) {
        await toast.promise(api.put(`/trusted-contacts/${editingContact.id}`, contactFormData), {
          loading: 'Updating contact...',
          success: 'Contact updated successfully!',
          error: 'Failed to update contact.'
        });
      } else {
        await toast.promise(api.post('/trusted-contacts', contactFormData), {
          loading: 'Creating contact...',
          success: 'Contact created successfully!',
          error: 'Failed to create contact.'
        });
      }
      fetchTrustedContacts();
      setIsContactModalOpen(false);
    } catch (err) {
      console.error(err);
    }
  };

  const handleContactDelete = async (id: number) => {
    try {
      await api.delete(`/trusted-contacts/${id}`);
      toast.success('Contact deleted!');
      fetchTrustedContacts();
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Failed to delete contact');
    }
  };

  const confirmContactDelete = async () => {
    if (contactToDelete !== null) {
      await handleContactDelete(contactToDelete);
      setContactToDelete(null);
    }
    setIsContactDeleteModalOpen(false);
  };

  const handleSavePaymentType = async () => {
    if (updateUser) {
      await updateUser({ payment_type: paymentType });
      setIsEditingPaymentType(false);
    }
  };

  const handleSaveInvoiceType = async () => {
    if (updateUser) {
      await updateUser({ default_invoice_type: invoiceType });
      setIsEditingInvoiceType(false);
    }
  };

  const handleSaveSendEmailSetting = async () => {
    if (updateUser) {
      await updateUser({ send_email_to_trusted_contact: sendEmailToTrustedContact });
      setIsEditingSendEmailSetting(false);
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
        My Profile
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
        <Box sx={{ flex: { xs: 1, md: '0 0 33%' } }}>
          <Card elevation={3}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar sx={{ width: 80, height: 80, margin: '0 auto 16px', bgcolor: 'primary.main' }}>
                <Person sx={{ fontSize: 50 }} />
              </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                {user?.first_name} {user?.last_name}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {user?.email}
              </Typography>
              {user?.external_client_id && (
                <Box sx={{ mt: 2, p: 1, backgroundColor: 'success.light', borderRadius: 1 }}>
                  <Typography variant="body2" color="success.contrastText" sx={{ fontWeight: 'bold' }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                    Connected to Sharewares Admin Service
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: 1 }}>
        <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
            <CardHeader
              title="My Addresses"
              action={
                <Button variant="outlined" startIcon={<Add />} onClick={handleAddNew}>
                  Add Address
                </Button>
              }
            />
            <CardContent>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : addresses.length === 0 ? (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ my: 4 }}>
                  You haven't added any addresses yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {addresses.map((addr) => (
                    <Card variant="outlined" key={addr.id}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              Address
                            </Typography>
                            <Typography>
                              {addr.address_line_1}, {addr.address_line_2}
                            </Typography>
                            <Typography>
                              {addr.city}, {addr.state} {addr.postal_code}
                            </Typography>
                            <Typography>{addr.country}</Typography>
                          </Box>
                          <Box>
                            <IconButton onClick={() => handleEdit(addr)}><Edit /></IconButton>
                            <IconButton onClick={() => {
                              setAddressToDelete(addr.id);
                              setIsConfirmModalOpen(true);
                            }}>
                              <Delete />
                            </IconButton>
                          </Box>
                        </Box>
                        {/* Show approved delivery fees if any */}
                        {(addr.approved_delivery_fee || addr.approved_delivery_fee_non_standard) && (
                          <Box sx={{ mt: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                            <Typography variant="caption" color="success.dark" sx={{ fontWeight: 'bold' }}>
                              Pre-approved Delivery Fees:
                            </Typography>
                            {addr.approved_delivery_fee && (
                              <Typography variant="caption" display="block">
                                Standard hours: ${(addr.approved_delivery_fee / 100).toFixed(2)}
                              </Typography>
                            )}
                            {addr.approved_delivery_fee_non_standard && (
                              <Typography variant="caption" display="block">
                                Non-standard hours: ${(addr.approved_delivery_fee_non_standard / 100).toFixed(2)}
                              </Typography>
                            )}
                            <Typography variant="caption" color="text.secondary" display="block">
                              (Set by admin)
                            </Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Paper>

          <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
            <CardHeader
              title="My Customer Contacts"
              action={
                <Button variant="outlined" startIcon={<Add />} onClick={handleContactAddNew}>
                  Add Contact
                </Button>
              }
            />
            <CardContent>
              {trustedContacts.length === 0 ? (
                <Typography variant="body1" color="text.secondary" align="center" sx={{ my: 4 }}>
                  You haven't added any customer contacts yet.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {trustedContacts.map((contact) => (
                    <Card variant="outlined" key={contact.id}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {contact.name}
                            </Typography>
                            {contact.title && (
                              <Typography variant="body2" color="text.secondary">
                                {contact.title}
                              </Typography>
                            )}
                            <Typography variant="body2">
                              {contact.email}
                            </Typography>
                              {contact.phone_number && (
                              <Typography variant="body2" color="text.secondary">
                                {contact.phone_number}
                              </Typography>
                            )}
                          </Box>
                          <Box>
                            <IconButton onClick={() => handleContactEdit(contact)}>
                              <Edit />
                            </IconButton>
                            <IconButton onClick={() => {
                              setContactToDelete(contact.id);
                              setIsContactDeleteModalOpen(true);
                            }}>
                              <Delete />
                            </IconButton>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </CardContent>
          </Paper>

          <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
            <CardHeader
              title="Other Settings"
            />
            <CardContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Default Payment Type:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    {isEditingPaymentType ? (
                      <FormControl fullWidth size="small">
                        <InputLabel id="payment-type-select-label">Payment Type</InputLabel>
                        <Select
                          labelId="payment-type-select-label"
                          id="payment-type-select"
                          value={paymentType}
                          label="Payment Type"
                          onChange={(e) => setPaymentType(e.target.value as string)}
                          renderValue={(selected) => (
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <FontAwesomeIcon icon={selected === 'e-transfer' ? 'money-bill-transfer' :
                                                    selected === 'EFT' ? 'building-columns' :
                                                    selected === 'cheque' ? 'money-check' :
                                                    selected === 'credit card (+2.9% fee)' ? 'credit-card' :
                                                    selected === 'other' ? 'ellipsis-h' : 'question-circle'} style={{ marginRight: '8px' }} />
                              {selected}
                            </Box>
                          )}
                        >
                          <MenuItem value="e-transfer"><FontAwesomeIcon icon="money-bill-transfer" style={{ marginRight: '8px' }} /> e-transfer</MenuItem>
                          <MenuItem value="EFT"><FontAwesomeIcon icon="building-columns" style={{ marginRight: '8px' }} /> EFT</MenuItem>
                          <MenuItem value="cheque"><FontAwesomeIcon icon="money-check" style={{ marginRight: '8px' }} /> cheque</MenuItem>
                          <MenuItem value="credit card (+2.9% fee)"><FontAwesomeIcon icon="credit-card" style={{ marginRight: '8px' }} /> credit card (+2.9% fee)</MenuItem>
                          <MenuItem value="other"><FontAwesomeIcon icon="ellipsis-h" style={{ marginRight: '8px' }} /> other</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <FontAwesomeIcon icon={user?.payment_type === 'e-transfer' ? 'money-bill-transfer' :
                                                user?.payment_type === 'EFT' ? 'building-columns' :
                                                user?.payment_type === 'cheque' ? 'money-check' :
                                                user?.payment_type === 'credit card (+2.9% fee)' ? 'credit-card' :
                                                user?.payment_type === 'other' ? 'ellipsis-h' : 'question-circle'} style={{ marginRight: '8px' }} />
                          {user?.payment_type || 'Not set'}
                        </Box>
                      </Typography>
                    )}
                    <Box sx={{ minWidth: isEditingPaymentType ? 'auto' : '24px' }}>
                      {isEditingPaymentType ? (
                        <Box>
                          <IconButton onClick={handleSavePaymentType} color="primary" size="small">
                            <CheckCircleOutlineIcon />
                          </IconButton>
                          <IconButton onClick={() => setIsEditingPaymentType(false)} color="error" size="small">
                            <CancelOutlinedIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <IconButton onClick={() => setIsEditingPaymentType(true)} size="small">
                          <Edit />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Invoice Type:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    {isEditingInvoiceType ? (
                      <FormControl fullWidth size="small">
                        <InputLabel id="invoice-type-select-label">Invoice Type</InputLabel>
                        <Select
                          labelId="invoice-type-select-label"
                          id="invoice-type-select"
                          value={invoiceType}
                          label="Invoice Type"
                          onChange={(e) => setInvoiceType(e.target.value as string)}
                        >
                          <MenuItem value="recurring">Recurring</MenuItem>
                          <MenuItem value="one-time">One-time</MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body1" sx={{ fontWeight: 'bold', flexGrow: 1 }}>
                        {user?.default_invoice_type || 'Not set'}
                      </Typography>
                    )}
                    <Box sx={{ minWidth: isEditingInvoiceType ? 'auto' : '24px' }}>
                      {isEditingInvoiceType ? (
                        <Box>
                          <IconButton onClick={handleSaveInvoiceType} color="primary" size="small">
                            <CheckCircleOutlineIcon />
                          </IconButton>
                          <IconButton onClick={() => setIsEditingInvoiceType(false)} color="error" size="small">
                            <CancelOutlinedIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <IconButton onClick={() => setIsEditingInvoiceType(true)} size="small">
                          <Edit />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Email Settings:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                    <FormControlLabel
                      control={
                        isEditingSendEmailSetting ? (
                          <Checkbox
                            checked={sendEmailToTrustedContact}
                            onChange={(e) => setSendEmailToTrustedContact(e.target.checked)}
                          />
                        ) : (
                          <Checkbox
                            checked={sendEmailToTrustedContact}
                            disabled
                          />
                        )
                      }
                      label="Send confirmation email to customer contact"
                      sx={{ flexGrow: 1 }}
                    />
                    <Box sx={{ minWidth: isEditingSendEmailSetting ? 'auto' : '24px' }}>
                      {isEditingSendEmailSetting ? (
                        <Box>
                          <IconButton onClick={handleSaveSendEmailSetting} color="primary" size="small">
                            <CheckCircleOutlineIcon />
                          </IconButton>
                          <IconButton onClick={() => setIsEditingSendEmailSetting(false)} color="error" size="small">
                            <CancelOutlinedIcon />
                          </IconButton>
                        </Box>
                      ) : (
                        <IconButton onClick={() => setIsEditingSendEmailSetting(true)} size="small">
                          <Edit />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </Box>

                <Box>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>Next-Day Delivery Approval:</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                      {user?.approved_for_next_day_delivery ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                          <CheckCircleOutlineIcon color="success" fontSize="small" />
                          <Typography variant="body2" color="success.dark" sx={{ fontWeight: 'bold' }}>
                            Approved for next-day delivery
                          </Typography>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1, bgcolor: 'warning.light', borderRadius: 1 }}>
                          <CancelOutlinedIcon color="warning" fontSize="small" />
                          <Typography variant="body2" color="warning.dark" sx={{ fontWeight: 'bold' }}>
                            Not approved for next-day delivery
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      (Contact admin to change)
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    {user?.approved_for_next_day_delivery 
                      ? "You can schedule deliveries starting tomorrow. For same-day delivery, add your request in the order notes." 
                      : "You can schedule deliveries starting 7 days from now. For same-day delivery, add your request in the order notes."}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Paper>
        </Box>
      </Box>
      <Modal 
        open={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box sx={modalStyle}>
          <Typography id="modal-title" variant="h6" component="h2">
            {editingAddress ? 'Edit Address' : 'Add New Address'}
          </Typography>
          <AddressForm
            address={editingAddress}
            onSubmit={handleFormSubmit}
            onCancel={() => setIsModalOpen(false)}
          />
        </Box>
      </Modal>
      <Modal open={isConfirmModalOpen} onClose={() => setIsConfirmModalOpen(false)}>
        <Box sx={{ ...modalStyle, width: { xs: '90%', sm: 300 }, maxWidth: 300 }}>
          <Typography variant="h6" component="h2">
            Confirm Deletion
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Are you sure you want to delete this address?
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" onClick={() => setIsConfirmModalOpen(false)}>Cancel</Button>
            <Button onClick={confirmDelete} variant="outlined" color="error">
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
      
      {/* Trusted Contact Modal */}
      <Modal 
        open={isContactModalOpen} 
        onClose={() => setIsContactModalOpen(false)} 
        aria-labelledby="contact-modal-title"
      >
        <Box sx={modalStyle}>
          <Typography id="contact-modal-title" variant="h6" component="h2">
            {editingContact ? 'Edit Customer Contact' : 'Add New Customer Contact'}
          </Typography>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              label="Name"
              value={contactFormData.name}
              onChange={(e) => setContactFormData({ ...contactFormData, name: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Title"
              value={contactFormData.title}
              onChange={(e) => setContactFormData({ ...contactFormData, title: e.target.value })}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label="Email"
              type="email"
              value={contactFormData.email}
              onChange={(e) => setContactFormData({ ...contactFormData, email: e.target.value })}
              fullWidth
              required
              sx={{ mb: 2 }}
            />
            <TextField
              label="Phone Number"
              value={contactFormData.phone_number}
              onChange={(e) => setContactFormData({ ...contactFormData, phone_number: e.target.value })}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="outlined" onClick={() => setIsContactModalOpen(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleContactFormSubmit} 
                variant="contained"
                disabled={!contactFormData.name.trim() || !contactFormData.email.trim()}
              >
                {editingContact ? 'Update' : 'Add'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      {/* Trusted Contact Delete Confirmation Modal */}
      <Modal open={isContactDeleteModalOpen} onClose={() => setIsContactDeleteModalOpen(false)}>
        <Box sx={{ ...modalStyle, width: { xs: '90%', sm: 300 }, maxWidth: 300 }}>
          <Typography variant="h6" component="h2">
            Confirm Deletion
          </Typography>
          <Typography sx={{ mt: 2 }}>
            Are you sure you want to delete this customer contact?
          </Typography>
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button variant="outlined" onClick={() => setIsContactDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={confirmContactDelete} variant="outlined" color="error">
              Delete
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ProfilePage; 